import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Camera, MapPin, X, ChevronLeft, ChevronRight, Instagram, Sparkles, Loader2 } from 'lucide-react';
import { TiltCard } from '../components/home/TiltCard';
import { ScrollDepthReveal } from '../components/home/ScrollDepthReveal';

const SubMergePage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchGallery = async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API_URL}/api/gallery/submerge`);
        if (r.ok) {
          const data = await r.json();
          setGalleryImages(
            (Array.isArray(data) ? data : []).map((item) => ({
              id: item.gallery_id,
              url: item.image_url,
              title: item.title || '',
              location: item.location || '',
              category: item.category || 'General',
              description: item.description || '',
            }))
          );
        } else {
          setGalleryImages([]);
        }
      } catch (e) {
        console.error('Failed to fetch submerge gallery', e);
        setGalleryImages([]);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoryList = Array.from(new Set(galleryImages.map((i) => i.category).filter(Boolean)));
  const categories = ['All', ...categoryList];
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredImages = activeCategory === 'All'
    ? galleryImages
    : galleryImages.filter((img) => img.category === activeCategory);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const nextIndex = (currentIndex + 1) % filteredImages.length;
    setCurrentIndex(nextIndex);
    setSelectedImage(filteredImages[nextIndex]);
  };

  const prevImage = () => {
    const prevIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    setCurrentIndex(prevIndex);
    setSelectedImage(filteredImages[prevIndex]);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1643913058691-e033a8ae026d?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80')] bg-cover bg-center opacity-15 grayscale" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0c]/80 via-black/60 to-[#0a0a0c]" />
          <div className="absolute top-20 left-10 w-96 h-96 bg-white/[0.05] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/[0.05] rounded-full blur-3xl" />

          <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-flex items-center px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full text-neutral-300 text-sm font-medium mb-6">
              <Camera size={14} className="mr-2" />Photography Collection
            </span>
            
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Sub<span className="bg-gradient-to-r from-white via-neutral-300 to-neutral-500 bg-clip-text text-transparent">Merge</span>
            </h1>
            
            <p className="text-xl text-neutral-400 max-w-3xl mx-auto leading-relaxed mb-8">
              A visual journey that explores the vibrant culture, rich heritage, and breathtaking landscapes of India, 
              presented through original and evocative images. Each photograph tells a unique story, 
              offering a glimpse into the diverse and dynamic tapestry that makes up the heart of this incredible country.
            </p>

            <a
              href="https://www.instagram.com/submerge.vistas/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-medium rounded-full hover:shadow-lg hover:shadow-white/20 hover:bg-neutral-200 transition-all duration-300 transform hover:scale-105"
            >
              <Instagram size={20} />
              Follow on Instagram
            </a>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-8 bg-black/50 backdrop-blur-sm sticky top-16 z-20 border-y border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-5 py-2 rounded-full font-medium transition-all duration-300 ${
                    activeCategory === category
                      ? 'bg-white text-black shadow-lg shadow-white/20'
                      : 'bg-white/10 text-neutral-300 hover:bg-white/20 hover:text-white'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery Grid */}
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-neutral-400" />
              </div>
            ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredImages.map((image, index) => (
                <ScrollDepthReveal key={image.id} delay={Math.min(index * 0.05, 0.3)}>
                  <TiltCard maxTilt={12} hoverScale={1.04} testId={`submerge-tilt-${index}`}>
                    <div
                      onClick={() => openLightbox(image, index)}
                      className="group relative cursor-pointer overflow-hidden rounded-2xl aspect-[4/3] bg-neutral-900"
                    >
                      {/* Image */}
                      <img
                        src={image.url}
                        alt={image.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        loading="lazy"
                      />
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      {/* Content */}
                      <div className="absolute inset-0 flex flex-col justify-end p-6 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                        <span className="inline-block px-3 py-1 bg-white/90 text-black text-xs font-medium rounded-full w-fit mb-2">
                          {image.category}
                        </span>
                        <h3 className="text-xl font-bold text-white mb-1">{image.title}</h3>
                        <p className="text-sm text-neutral-300 flex items-center gap-1">
                          <MapPin size={14} />
                          {image.location}
                        </p>
                      </div>

                      {/* Corner Decoration */}
                      <div className="absolute top-4 right-4 w-10 h-10 border-t-2 border-r-2 border-white/30 rounded-tr-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="absolute bottom-4 left-4 w-10 h-10 border-b-2 border-l-2 border-white/30 rounded-bl-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </div>
                  </TiltCard>
                </ScrollDepthReveal>
              ))}
            </div>
            )}
          </div>
        </section>

        {/* Quote Section */}
        <section className="py-16 lg:py-24 relative overflow-hidden bg-black">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1731317607760-7a21a1bb24be?w=1920')] bg-cover bg-center opacity-10 grayscale" />
          
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Sparkles className="w-12 h-12 text-white mx-auto mb-6" />
            <blockquote className="text-2xl lg:text-3xl text-white font-light italic leading-relaxed mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              "Whether you are an art enthusiast, a culture aficionado, or simply someone who appreciates the beauty of photography, 
              SubMerge invites you to experience India like never before."
            </blockquote>
            <p className="text-neutral-400 font-medium">— Bivas Mishra</p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-10 lg:p-14 border border-white/10">
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Explore More
              </h2>
              <p className="text-neutral-400 text-lg mb-8 max-w-2xl mx-auto">
                Follow SubMerge on Instagram for daily glimpses into India's incredible diversity, 
                from ancient temples to modern cities.
              </p>
              <a
                href="https://www.instagram.com/submerge.vistas/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-black font-bold rounded-full hover:shadow-lg hover:shadow-white/20 hover:bg-neutral-200 transition-all duration-300 transform hover:scale-105"
              >
                <Instagram size={22} />
                @submerge.vistas
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button 
            onClick={closeLightbox}
            className="absolute top-6 right-6 p-2 text-white/70 hover:text-white transition-colors z-50"
          >
            <X size={32} />
          </button>

          {/* Navigation */}
          <button 
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 lg:left-8 p-3 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors"
          >
            <ChevronLeft size={28} />
          </button>
          
          <button 
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 lg:right-8 p-3 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors"
          >
            <ChevronRight size={28} />
          </button>

          {/* Image */}
          <div 
            className="max-w-5xl max-h-[80vh] mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.url}
              alt={selectedImage.title}
              className="max-w-full max-h-[70vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-center">
              <h3 className="text-2xl font-bold text-white">{selectedImage.title}</h3>
              <p className="text-neutral-400 flex items-center justify-center gap-2 mt-2">
                <MapPin size={16} />
                {selectedImage.location}
              </p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default SubMergePage;
